import { SitePro } from '../entities/crm/sites/site.pro';
import { SiteTiny } from '../entities/shared/site';
import { Address } from '../entities/shared/address';

export class AddressUtility {

    public static formatAddress(address: Address): string {
        if (!address) return '';

        if (address.name)
            return address.name;

        const street1 = address.street1 ? address.street1.trim() : '';
        const street2 = address.street2 ? address.street2.trim() : '';
        const suburb = address.suburb ? address.suburb.trim() : '';

        const state = address?.state?.trim() ?? '';
        const county = address?.county?.trim() ?? '';

        const postcode = address.postcode
            ? address.postcode?.toString().trim()
            : '';

        const postalCode = address?.postalCode?.trim() ?? '';

        const safePostalCode = postalCode
            ? postalCode
            : postcode;

        const components = [];
        if (street2) components.push(street2);
        if (street1) components.push(street1);
        if (suburb) components.push(suburb);
        if (county) components.push(county);
        if (state) components.push(state);
        if (safePostalCode) components.push(safePostalCode);

        // Coordinate-only addresses (AddressType.Coordinates) store nothing but
        // lat/lgn, so fall back to the coordinates rather than an empty label.
        // 5 decimal places ≈ 1 metre precision.
        if (!components.length && typeof address.lat === 'number' && typeof address.lgn === 'number') {
            return `${address.lat.toFixed(5)}, ${address.lgn.toFixed(5)}`;
        }

        return components.join(' ')?.trim();
    }

    /** The address as two display lines — street, then suburb/state/postcode — instead of one wrapped string. Ignores `address.name`. */
    public static formatAddressLines(address: Address): { street: string; locality: string } {

        const part = (value: unknown) => value == null ? '' : value.toString().trim();
        const street = [address?.street2, address?.street1].map(part).filter(Boolean).join(' ');
        const locality = [address?.suburb, address?.county, address?.state, part(address?.postalCode) || part(address?.postcode)].map(part).filter(Boolean).join(' ');
        return { street, locality };
    }

    public static isEqual(address1: Address, address2: Address): boolean {

        if (!address1 || !address2) return false;

        if (address1?.street1?.toLowerCase() !== address2?.street1?.toLowerCase())
            return false;

        if (address1?.suburb?.toLowerCase() !== address2?.suburb?.toLowerCase())
            return false;

        if (address1?.state?.toLowerCase() !== address2?.state?.toLowerCase())
            return false;

        if (address1?.postcode !== address2?.postcode)
            return false;

        if (address1.lat !== address2.lat)
            return false;

        if (address1.lgn !== address2.lgn)
            return false;

        if (!address1.lat || !address2.lat)
            return false;

        if (!address1.lgn || !address2.lgn)
            return false;

        return true;
    }

    public static getAddressName(address: Address) {
        //Similar to the above method, but we do not want to show both the site name and address name
        const addressName = address?.name ?? AddressUtility.formatAddress(address);

        return addressName;
    }

    public static getSiteName(site: SitePro | SiteTiny, notSetText: string = ''): string {

        if (!site) {
            return notSetText;
        }

        const addressName = this.getAddressName(site.address);
        const siteName = site.name?.trim();
        if (siteName && addressName && siteName !== addressName) {
            return `${addressName} (${siteName})`;
        }

        return addressName || siteName || notSetText;
    }

    /**
     * Whether a site's name says nothing the address does not already say.
     *
     * Most sites are named after their address ("18 Grove Avenue", "18 Grove Ave, Narwee NSW"), so a
     * display that prints the name above the address lines would repeat itself. An exact `!==` check
     * misses those: the name and the formatted address differ only in punctuation, case, spacing, or
     * by how much of the locality they carry. Comparison is therefore on a normalised form, and a
     * name that merely BEGINS with the street is derived too — "18 Grove Avenue" against
     * "18 Grove Avenue Narwee NSW 2209" is the common shape.
     *
     * A street-less address (coordinates, a bare ref) can derive nothing, so its name always stands.
     */
    public static isNameDerivedFromAddress(name: string | null | undefined, address: Address | null | undefined): boolean {

        const normalisedName = AddressUtility.normaliseForComparison(name);
        if (!normalisedName || !address) return false;

        const normalisedAddress = AddressUtility.normaliseForComparison(AddressUtility.formatAddress(address));
        if (normalisedAddress && normalisedName === normalisedAddress) return true;

        const normalisedStreet = AddressUtility.normaliseForComparison(address.street1);
        if (!normalisedStreet) return false;

        if (normalisedName.startsWith(normalisedStreet)) return true;

        // Some sites are named "<street>, <suburb>" while street1 holds only the street. Comparing
        // against street+suburb catches that without letting a genuinely different name through.
        const normalisedStreetAndSuburb = AddressUtility.normaliseForComparison(`${address.street1} ${address.suburb ?? ''}`);
        return normalisedStreetAndSuburb !== normalisedStreet && normalisedName.startsWith(normalisedStreetAndSuburb);
    }

    /**
     * Reduce address text to the characters that carry identity.
     *
     * Case, punctuation and spacing are formatting, not identity — and neither are the sub-dwelling
     * words, because the same dwelling is written "Unit 9 36 Cromwell Rd", "U9 36 Cromwell Rd" and
     * "9/36 Cromwell Rd" depending on who typed it. Stripping those words (word-bounded, before
     * punctuation goes, so "U9" still loses its "u") and then every non-alphanumeric character
     * collapses all three to "936cromwellrd".
     */
    private static normaliseForComparison(value: string | null | undefined): string {

        if (!value) return '';

        return value
            .toLowerCase()
            .replace(/\b(unit|u|shop|lot|level|suite)\b/g, ' ')
            .replace(/\bu(?=\d)/g, ' ')
            .replace(/[^a-z0-9]/g, '');
    }
}
